import React, { useEffect, useMemo, useState } from 'react'
import {
  Award,
  CheckCircle2,
  Edit3,
  Loader2,
  MessageSquare,
  Save,
  Star,
  UserCircle
} from 'lucide-react'

import { apiClient } from '@/lib/api'

interface EvaluationFormProps {
  sourceTable: 'code_submissions' | 'interviews_responses' | 'system_design_responses' | 'whiteboard_responses' | 'uml_design_responses' | 'data_modeling_responses'
  sourceRecordUuid: string
  candidateUuid: string
  interviewUuid?: string
  title: string
  onSave?: () => void
}

interface Evaluation {
  uuid: string
  evaluator_feedback?: string
  evaluator_marks?: number
  evaluated_at?: string
  evaluated_by?: number
  evaluator_uuid?: string
  evaluator_first_name?: string
  evaluator_last_name?: string
  evaluator_email?: string
}

const getScoreColor = (score: number) => {
  if (score >= 90) return 'text-green-600 bg-green-100'
  if (score >= 80) return 'text-blue-600 bg-blue-100'
  if (score >= 70) return 'text-yellow-600 bg-yellow-100'
  return 'text-red-600 bg-red-100'
}

const resolveUserUuid = (user: any): string | null => {
  if (!user) return null
  const candidates = [user.uuid, user.user_uuid, user.id, user.userId]
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) return candidate
  }
  return null
}

export default function EvaluationForm({
  sourceTable,
  sourceRecordUuid,
  candidateUuid,
  interviewUuid,
  title,
  onSave
}: EvaluationFormProps) {
  const [evaluations, setEvaluations] = useState<Evaluation[]>([])
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null)
  const [feedback, setFeedback] = useState('')
  const [marks, setMarks] = useState<number | ''>('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentUser, setCurrentUser] = useState<any>(null)
  const [userLoading, setUserLoading] = useState(true)
  const [isEditing, setIsEditing] = useState(false)

  useEffect(() => {
    loadEvaluations()
  }, [sourceTable, sourceRecordUuid, interviewUuid])

  useEffect(() => {
    loadCurrentUser()
  }, [])

  const loadCurrentUser = async () => {
    try {
      setUserLoading(true)
      const response = await apiClient.getCurrentUser()
      if (response.success && response.data) {
        setCurrentUser(response.data)
      } else if (typeof window !== 'undefined') {
        const stored = localStorage.getItem('currentUser')
        if (stored) {
          try {
            setCurrentUser(JSON.parse(stored))
          } catch (err) {
            console.warn('Failed to parse currentUser from storage', err)
          }
        }
      }
    } catch (err) {
      console.error('Failed to load current user:', err)
    } finally {
      setUserLoading(false)
    }
  }

  const loadEvaluations = async () => {
    try {
      setLoading(true)
      let fetched: Evaluation[] = []

      if (interviewUuid) {
        const listResponse = await apiClient.getInterviewEvaluations(interviewUuid)
        if (listResponse.success && Array.isArray(listResponse.data)) {
          fetched = listResponse.data
            .filter((item: any) => item.source_table === sourceTable && item.source_record_uuid === sourceRecordUuid)
        }
      }

      if (!fetched.length) {
        const singleResponse = await apiClient.getEvaluation(sourceTable, sourceRecordUuid)
        if (singleResponse.success && Array.isArray(singleResponse.data)) {
          fetched = singleResponse.data as any
        }
      }

      setEvaluations(fetched)
    } catch (err) {
      console.error('Failed to load evaluations:', err)
      setEvaluations([])
    } finally {
      setLoading(false)
    }
  }

  const saveEvaluation = async () => {
    try {
      setSaving(true)
      setError(null)

      const evaluationData = {
        evaluator_feedback: feedback.trim() || undefined,
        evaluator_marks: marks !== '' ? Number(marks) : undefined
      }

      let response
      if (evaluation?.uuid) {
        response = await apiClient.updateEvaluation(evaluation.uuid, evaluationData)
      } else {
        response = await apiClient.createEvaluation({
          source_table: sourceTable,
          source_record_uuid: sourceRecordUuid,
          candidate_uuid: candidateUuid,
          interview_uuid: interviewUuid,
          ...evaluationData
        })
      }

      if (response.success) {
        await loadEvaluations()
        setIsEditing(false)
        onSave?.()
      } else {
        setError(response.error || 'Failed to save evaluation')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save evaluation')
    } finally {
      setSaving(false)
    }
  }

  const userUuid = useMemo(() => resolveUserUuid(currentUser), [currentUser])

  useEffect(() => {
    if (userLoading) return

    if (userUuid) {
      const myEvaluation = evaluations.find(ev => {
        return (ev.evaluator_uuid && ev.evaluator_uuid === userUuid)
      })

      setEvaluation(myEvaluation || null)

      if (!isEditing) {
        if (myEvaluation) {
          setFeedback(myEvaluation.evaluator_feedback || '')
          const existingMarks = myEvaluation.evaluator_marks
          setMarks(existingMarks === undefined || existingMarks === null ? '' : Number(existingMarks))
        } else {
          setFeedback('')
          setMarks('')
        }
      }
    } else if (!isEditing) {
      setEvaluation(null)
      setFeedback('')
      setMarks('')
    }
  }, [evaluations, userUuid, userLoading, isEditing])

  const evaluatorUuid = evaluation?.evaluator_uuid || null

  const canEdit = !evaluation || (userUuid !== null && evaluatorUuid !== null && userUuid === evaluatorUuid)

  const displayName = evaluation
    ? evaluatorUuid !== null && userUuid !== null && evaluatorUuid === userUuid
      ? 'You'
      : [evaluation.evaluator_first_name, evaluation.evaluator_last_name].filter(Boolean).join(' ') || evaluation.evaluator_email || 'Unknown evaluator'
    : currentUser
      ? 'You'
      : 'Evaluator'

  const showForm = (evaluation && isEditing && canEdit) || (!evaluation && canEdit && isEditing)
  const otherEvaluations = evaluations.filter(ev => ev.uuid !== evaluation?.uuid)

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-blue-600 mr-2" />
          <span className="text-gray-600">Loading evaluation...</span>
        </div>
      </div>
    )
  }

  const handleCancelEdit = () => {
    if (evaluation) {
      setFeedback(evaluation.evaluator_feedback || '')
      const existingMarks = evaluation.evaluator_marks
      setMarks(existingMarks === undefined || existingMarks === null ? '' : Number(existingMarks))
      setIsEditing(false)
      setError(null)
    } else {
      setFeedback('')
      setMarks('')
    }
  }

  const getFeedbackLength = feedback.trim().length

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Award className="w-5 h-5 text-purple-600" />
          <h3 className="text-lg font-medium text-gray-900">Evaluation: {title}</h3>
        </div>
        {evaluation?.evaluated_at && (
          <span className="text-sm text-gray-500">
            Last updated: {new Date(evaluation.evaluated_at).toLocaleString()}
          </span>
        )}
      </div>

      {evaluation && !showForm && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <UserCircle className="w-5 h-5 text-gray-500" />
              <div>
                <p className="text-sm text-gray-500">Evaluator</p>
                <p className="text-sm font-medium text-gray-900">{displayName}</p>
              </div>
            </div>
            {evaluation.evaluator_marks !== undefined && evaluation.evaluator_marks !== null && (
              <span className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-semibold ${getScoreColor(Number(evaluation.evaluator_marks))}`}>
                <Star className="w-4 h-4" />
                <span>{evaluation.evaluator_marks}/100</span>
              </span>
            )}
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
            {evaluation.evaluator_feedback ? (
              <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{evaluation.evaluator_feedback}</p>
            ) : (
              <p className="text-sm text-slate-500 italic">No written feedback provided.</p>
            )}
          </div>

          {canEdit ? (
            <div className="flex items-center justify-between text-xs text-gray-500">
              <p>You can update this evaluation.</p>
              <button
                onClick={() => {
                  setFeedback(evaluation.evaluator_feedback || '')
                  const existingMarks = evaluation.evaluator_marks
                  setMarks(existingMarks === undefined || existingMarks === null ? '' : Number(existingMarks))
                  setIsEditing(true)
                }}
                className="inline-flex items-center space-x-1 px-3 py-1 text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                <Edit3 className="w-4 h-4" />
                <span>Edit</span>
              </button>
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic">Only the original evaluator can modify this feedback.</p>
          )}
        </div>
      )}

      {showForm && (
        <>
          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Marks (0-100)</label>
              <div className="flex items-center space-x-3">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={marks}
                  onChange={(e) => setMarks(e.target.value === '' ? '' : Number(e.target.value))}
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0-100"
                />
                {marks !== '' && (
                  <span className={`px-2 py-1 rounded-full text-sm font-medium ${getScoreColor(Number(marks))}`}>
                    {marks}/100
                  </span>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <MessageSquare className="w-4 h-4 inline mr-1" />
                Feedback & Comments
              </label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                placeholder="Enter your evaluation feedback, comments, and observations..."
              />
              <div className="text-xs text-gray-500 mt-1">
                {getFeedbackLength} characters
              </div>
            </div>

            <div className="flex items-center justify-end space-x-2">
              {evaluation && (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 rounded-lg border border-gray-300"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={saveEvaluation}
                disabled={saving || (!feedback.trim() && marks === '')}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                <span>{evaluation?.uuid ? 'Update' : 'Save'} Evaluation</span>
              </button>
            </div>
          </div>
        </>
      )}

      {!evaluation && !showForm && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">No evaluation submitted yet.</p>
          {userLoading ? (
            <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
          ) : (
            <button
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100"
            >
              <CheckCircle2 className="w-4 h-4" />
              <span>Add Evaluation</span>
            </button>
          )}
        </div>
      )}

      {otherEvaluations.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-gray-700">Other Evaluator Feedback</h4>
          {otherEvaluations.map(other => {
            const name = [other.evaluator_first_name, other.evaluator_last_name].filter(Boolean).join(' ') || other.evaluator_email || 'Evaluator'
            return (
              <div key={other.uuid} className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <UserCircle className="w-4 h-4" />
                    <span>{name}</span>
                  </div>
                  {other.evaluator_marks !== undefined && other.evaluator_marks !== null && (
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getScoreColor(Number(other.evaluator_marks))}`}>
                      {other.evaluator_marks}/100
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {other.evaluator_feedback || 'No comments provided.'}
                </p>
                {other.evaluated_at && (
                  <p className="text-xs text-gray-400 text-right">{new Date(other.evaluated_at).toLocaleString()}</p>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
